import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';

import { ComponentAccess } from '../../decorators/component-access.decorator';
import { CaptchaService } from '../../services/captcha/captcha.service';
import { CaptchaTokenStore } from '../../services/captcha/captcha-token.store';
import { LogFactory } from '../../services/logger/log-factory';
import { AppConfigService } from '../../config/app.config.service';
import { ThemeService } from '../../../app/theme/theme.service';

// One immediate retry with a brand-new widget smooths a transient blip without
// looping. It only applies to codes a retry could plausibly clear.
const RETRYABLE_ERROR_RETRIES = 1;
// Hard ceiling in case neither a callback nor an error-callback ever fires
// (e.g. the challenge iframe never loads on a dead network) so verify() can't
// hang the sign-in button forever.
const RENDER_TIMEOUT_MS = 30_000;
// Cloudflare error codes that mean THE WIDGET IS MISCONFIGURED — sitekey wrong,
// unknown or disabled, or the hostname is not on the key's allowlist. A retry
// re-runs the identical config and fails identically, so these settle on the
// first attempt and are logged at error: they are a deployment defect, not a
// user or runtime problem.
// developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
const CONFIG_ERROR_CODES = new Set([
  '110100', // invalid sitekey
  '110110', // sitekey not found
  '110200', // domain not authorised for this sitekey
  '400020', // invalid sitekey
  '400070', // sitekey disabled
]);

type TurnstileOutcome = { token: string } | { token: null; retryable: boolean };

/**
 * Invisible, single-use Turnstile captcha.
 *
 * Every verify() renders a brand-new widget into a throwaway element, waits for
 * Turnstile's own callback to deliver a token — Turnstile owns all iframe
 * messaging, so we never poke it with reset()/execute() — hands that token to
 * the credential request, then removes the widget. Nothing is parked between attempts, so
 * no widget can go stale in an idle tab or accumulate in the long-lived api.js
 * runtime (ADR-0060).
 *
 * verify() FAILS CLOSED on every path. There is deliberately no "give up and
 * let them through after N attempts" escape: repeating a failed challenge must
 * never be a way past it. Cloudflare classifies the 600xxx/300xxx codes this
 * once failed open on as "generic challenge failure — bot behaviour detected",
 * so opening on them handed the check away on exactly the signal it exists to
 * raise (ADR-0060, 2026-08-25 revision).
 *
 * Dev note: challenges intermittently fail with error 600010 while browser
 * DevTools is open — Turnstile anti-tamper, not a bug here. Sign in with
 * DevTools closed, or run against an environment with captcha disabled.
 */
@Component({
  selector: 'app-captcha',
  template: `<div #captchaContainer></div>`,
  standalone: true,
})
@ComponentAccess('CaptchaComponent')
export class CaptchaComponent implements OnInit, OnDestroy {
  private readonly renderer = inject(Renderer2);
  private readonly captchaService = inject(CaptchaService);
  private readonly captchaTokens = inject(CaptchaTokenStore);
  private readonly configService = inject(AppConfigService);
  private readonly themeService = inject(ThemeService);
  private readonly logger = inject(LogFactory).createLogger(CaptchaComponent);

  @ViewChild('captchaContainer', { static: true })
  captchaContainer!: ElementRef<HTMLElement>;

  private isCaptchaEnabled = false;
  private verifying = false;

  ngOnInit(): void {
    this.isCaptchaEnabled = this.configService.isCaptchaEnabled;
  }

  ngOnDestroy(): void {
    // Nothing is parked; a widget only lives inside a runChallenge() call, which
    // always tears itself down. This flag just short-circuits a verify() that is
    // still awaiting when the host page is destroyed.
    this.verifying = false;
  }

  /**
   * Runs a fresh challenge and holds the resulting token for the credential
   * request that follows, where the API checks it. Resolves true when captcha
   * is disabled by config or Cloudflare issued a token; false otherwise.
   */
  async verify(): Promise<boolean> {
    if (!this.isCaptchaEnabled) return true;
    if (this.verifying) return false; // ignore a double submit

    this.verifying = true;
    try {
      const outcome = await this.runChallengeWithRetry();
      if (outcome.token === null) return false;
      // The API checks the token on the credential request itself; hand it
      // to the interceptor that attaches it there.
      this.captchaTokens.set(outcome.token);
      return true;
    } finally {
      this.verifying = false;
    }
  }

  /**
   * Runs the challenge, retrying once if the first attempt died for a reason a
   * retry could clear. The retry runs on a brand-new widget AND a freshly
   * loaded api.js runtime, ruling out degraded runtime state. One retry is
   * enough: a persistent failure is either environmental or a misconfiguration,
   * and neither is won by asking again.
   */
  private async runChallengeWithRetry(): Promise<TurnstileOutcome> {
    let outcome = await this.runChallenge();
    let retries = RETRYABLE_ERROR_RETRIES;
    while (outcome.token === null && outcome.retryable && retries-- > 0) {
      this.logger.warn(
        'Captcha attempt failed; retrying with a fresh Turnstile runtime.',
      );
      try {
        await this.captchaService.refreshRuntime();
      } catch (error) {
        this.logger.error('Turnstile runtime refresh failed', error);
        break;
      }
      outcome = await this.runChallenge();
    }
    return outcome;
  }

  /**
   * Renders one throwaway widget, resolves with its token (or a tokenless
   * outcome), and always removes the widget + element afterwards.
   */
  private async runChallenge(): Promise<TurnstileOutcome> {
    // An enabled captcha with no site key renders a widget that can only ever
    // fail. Say so once, precisely, instead of leaving a generic "we couldn't
    // verify your browser" as the only trace of a broken deployment.
    const sitekey = this.configService.turnstileSiteKey;
    if (!sitekey) {
      this.logger.error(
        'Captcha is enabled but no Turnstile site key is configured; sign-in cannot succeed until one is set',
      );
      return { token: null, retryable: false };
    }

    try {
      await this.captchaService.loadCaptcha();
    } catch (error) {
      this.logger.error('Turnstile script failed to load', error);
      return { token: null, retryable: false };
    }

    const turnstile = (window as any).turnstile;
    if (!turnstile || typeof turnstile.render !== 'function') {
      this.logger.error(
        'Turnstile object or render function not available after load.',
      );
      return { token: null, retryable: false };
    }

    const host = this.captchaContainer.nativeElement;
    const widgetEl = this.renderer.createElement('div') as HTMLElement;
    this.renderer.appendChild(host, widgetEl);

    let widgetId: string | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      return await new Promise<TurnstileOutcome>((resolve) => {
        let settled = false;
        const settle = (outcome: TurnstileOutcome): void => {
          if (settled) return;
          settled = true;
          resolve(outcome);
        };

        timer = setTimeout(() => {
          this.logger.warn(
            'Captcha challenge timed out before a token was issued.',
          );
          settle({ token: null, retryable: false });
        }, RENDER_TIMEOUT_MS);

        try {
          widgetId = turnstile.render(widgetEl, {
            sitekey,
            theme: this.themeService.isDarkMode() ? 'dark' : 'light',
            // Invisible unless the challenge needs interaction; the challenge
            // runs on render and the callback delivers the token when the
            // iframe is genuinely ready — we never message it ourselves.
            appearance: 'interaction-only',
            retry: 'auto',
            'retry-interval': 8000,
            callback: (token: string) => settle({ token }),
            'error-callback': (errorCode: string) => {
              const isConfigError = CONFIG_ERROR_CODES.has(errorCode);
              if (isConfigError) {
                this.logger.error(
                  'Turnstile rejected this widget configuration; check the site key and the hostnames allowed for it',
                  { errorCode, sitekey },
                );
              } else {
                this.logger.warn('Turnstile challenge failed', { errorCode });
              }
              settle({ token: null, retryable: !isConfigError });
              return true; // we render our own failure message instead
            },
            'timeout-callback': () => {
              // Interactive challenge sat unanswered — the user, not the
              // runtime, so fail closed with no retry.
              settle({ token: null, retryable: false });
            },
            'unsupported-callback': () => {
              this.logger.warn('Turnstile is not supported by this browser');
              settle({ token: null, retryable: false });
            },
          });
        } catch (error) {
          this.logger.error('Error rendering captcha challenge', error);
          settle({ token: null, retryable: false });
        }
      });
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      if (widgetId !== undefined && turnstile.remove) {
        try {
          turnstile.remove(widgetId);
        } catch {
          // Already gone — nothing to release.
        }
      }
      if (widgetEl.parentNode === host) {
        this.renderer.removeChild(host, widgetEl);
      }
    }
  }
}
